import React, { useState, useRef } from "react";
import { PorfolioData } from "../../data/porfolioData";

export default function ProjectSection() {
    const [current, setCurrent] = useState(0);
    const [fade, setFade] = useState(false);
    const [direction, setDirection] = useState("next");
    const total = PorfolioData.length;
    
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);

    const goTo = (idx, dir = "next") => {
        if (idx < 0) idx = total - 1;
        if (idx >= total) idx = 0;

        setDirection(dir);
        setFade(true);
        setTimeout(() => {
            setCurrent(idx);
            setFade(false);
        }, 200);
    };

    const next = () => goTo(current + 1, "next");
    const prev = () => goTo(current - 1, "prev");

    // Touch handlers for mobile swipe
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        const swipeThreshold = 50; // minimum distance for swipe
        const diff = touchStartX.current - touchEndX.current;

        if (Math.abs(diff) > swipeThreshold) {
            if (diff > 0) {
                // Swiped left, go to next
                next();
            } else {
                // Swiped right, go to previous
                prev();
            }
        }
    };

    const transitionClass = fade
        ? direction === "next"
            ? "opacity-0 translate-x-10"
            : "opacity-0 -translate-x-10"
        : "opacity-100 translate-x-0";

    return (
        <section className="px-4 py-12 bg-gray-50 text-black lg:px-14">
            <div className="container mx-auto max-w-7xl">
                <div className="text-center max-w-3xl mx-auto mb-10">
                    <h2 className="text-primary font-bold text-2xl sm:text-3xl md:text-4xl leading-snug uppercase">
                        Projects & Showcases
                    </h2>
                    <p className="text-sm sm:text-base md:text-lg mt-3 text-gray-800">
                        Explore what the Tech Space community builds, from intuitive UI/UX
                        designs and engaging graphics to mobile and web applications. Every
                        project showcases practical learning, creativity, and collaboration.
                    </p>
                </div>

                <div className="relative flex flex-col items-center">
                    <div className="flex items-center justify-center w-full mt-6 sm:mt-10 mb-10">
                        <button
                            onClick={prev}
                            className="hidden sm:block p-2 sm:p-3 rounded-full bg-gray-200 hover:bg-gray-300 mr-2 disabled:opacity-50"
                            aria-label="Previous"
                        >
                            &#8592;
                        </button>

                        <div
                            className={`transition-all duration-300 ease-in-out transform w-full mb-10 flex justify-center items-center ${transitionClass}`}
                            style={{ minHeight: 350 }}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                        >
                            <article className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition w-full max-w-[340px] sm:max-w-md md:max-w-lg h-[420px] sm:h-[440px] md:h-[480px] flex flex-col">
                                <div className="relative h-2/5 sm:h-1/2 md:h-1/2">
                                    <img
                                        src={PorfolioData[current].image}
                                        alt={PorfolioData[current].title}
                                        loading="lazy"
                                        className="object-cover w-full h-full"
                                    />
                                    <span className="absolute left-3 top-3 bg-blue-100 text-primary px-3 py-1 rounded-full text-xs font-semibold">
                                        {PorfolioData[current].tag}
                                    </span>
                                </div>
                                <div className="p-3 sm:p-5">
                                    <h3 className="text-primary font-bold text-base sm:text-lg md:text-xl">
                                        {PorfolioData[current].title}
                                    </h3>
                                    <p className="mt-2 text-xs sm:text-sm md:text-base text-gray-700">
                                        {PorfolioData[current].desc}
                                    </p>
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {PorfolioData[current].tech.map((t) => (
                                            <span
                                                key={t}
                                                className="px-2 py-1 bg-gray-100 rounded-md text-xs border border-gray-100"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                    <a
                                        href={PorfolioData[current].url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-4 inline-flex items-center gap-2 text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2  hover:bg-purple-200 transition"
                                    >
                                        View Project
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M14 5l7 7m0 0l-7 7m7-7H3"
                                            />
                                        </svg>
                                    </a>
                                </div>
                            </article>
                        </div>

                        <button
                            onClick={next}
                            className="hidden sm:block p-2 sm:p-3 rounded-full bg-gray-200 hover:bg-gray-300 ml-2 disabled:opacity-50"
                            aria-label="Next"
                        >
                            &#8594;
                        </button>
                    </div>

                    <div className="flex justify-center mt-4 sm:mt-6 gap-2 z-10">
                        {PorfolioData.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => goTo(idx, idx > current ? "next" : "prev")}
                                className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full border-2 border-primary focus:outline-none transition-all ${current === idx ? "bg-primary" : "bg-gray-300"
                                    }`}
                                aria-label={`Go to slide ${idx + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}